// ------------- Component for the news panel of the application -------------------
// ----------------------------------------------------------------------------------

// import necessary libraries and hooks
import React from 'react';
import { useNewsData } from '../hooks/useNewsData';
import { useNewsSentiment } from '../hooks/useNewsSentiment';
import './NewsPanel.css';

// Component to display recent news articles related to the stock market, with sentiment analysis
const NewsPanel = ({ symbol, timeframe = 'MAX' }) => {
  const { data: articles, loading, error, refetch } = useNewsData(20, timeframe); // collect the news articles using the hook
  const { sentiments } = useNewsSentiment(articles); // fetch FinBERT sentiment whenever articles change

  // function to format the time since the article was published
  const formatTimeAgo = (dateString) => { // function to format the time since the article was published
    const date = new Date(dateString); // convert the date string to a date object
    const now = new Date(); // get the current date
    const diffMs = now - date; // calculate the difference in milliseconds
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60)); // convert milliseconds to hours
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24)); // convert milliseconds to days

    if (diffDays > 7) return date.toLocaleDateString(); // if more than a week ago show the date
    if (diffDays > 0) return `${diffDays}d ago`; // if more than a day ago show the days ago
    if (diffHours > 0) return `${diffHours}h ago`; // if more than an hour ago show the hours ago
    return 'Just now'; // otherwise show just now - won't happen in my version but kept for completeness - needs a more accurate api
  };

  if (loading) { // if the data is loading show a loading screen
    return (
      <div className="news-panel">
        <div className="news-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h3>Market News</h3>
            <span className="news-source">Source: New York Times - Articles from the last month of the period</span>
          </div>
        </div>
        <div style={{ textAlign: 'center', padding: '20px', color: '#9CA3AF' }}>
          <p>Loading news...</p>
          <p style={{ fontSize: '11px', marginTop: '10px' }}>This may take a moment due to semantic scoring</p>
        </div>
      </div>
    );
  }

  if (error) { // if there is an error in the collection return a message saying the collection has failed
    return (
      <div className="news-panel">
        <div className="news-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h3>Market News</h3>
            <span className="news-source">Source: New York Times - Articles from the last month of the period</span>
          </div>
        </div>
        <div style={{ textAlign: 'center', padding: '20px', color: '#EF4444' }}>
          <p>{error}</p>
          <button 
            onClick={refetch}
            style={{
              marginTop: '10px',
              padding: '8px 16px',
              background: '#06B6D4',
              color: '#0B1220',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!articles || articles.length === 0) { // if there is no data collected show that there is no data
    return (
      <div className="news-panel">
        <div className="news-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h3>Market News</h3>
            <span className="news-source">Source: New York Times - Articles from the last month of the period</span>
          </div>
        </div>
        <div style={{ textAlign: 'center', padding: '20px', color: '#9CA3AF' }}>
          <p>No news articles available</p>
          <p style={{ fontSize: '11px', marginTop: '10px' }}>Check your NEWS_API_KEY in .env</p>
        </div>
      </div>
    );
  }

  return ( // if the data has been collected successful show the data
    <div className="news-panel">
      <div className="news-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h3>Market News ({articles.length})</h3>
          <span className="news-source">Source: New York Times - Articles from the last month of the period</span>
        </div>
      </div>
      <div className="news-list">
        {articles.map((article, index) => ( // loop through all articles and display them to the dashboard
          <a 
            key={index} 
            href={article.web_url}  // add in the link to the article
            target="_blank" 
            rel="noopener noreferrer"
            className="news-item"
            style={{ textDecoration: 'none' }}
          >
            <div className={`sentiment-indicator ${sentiments[index] || 'neutral'}`}></div> {/* set sentiment indicator bar */}
            <div className="news-content">
              <h4>{article.headline}</h4>
              {article.abstract && ( // only show the abstract if it exists
                <p className="news-abstract">{article.abstract}</p> // display the article abstract
              )}
              <div className="news-meta">
                <span className="source">{article.section || 'NYT'}</span> {/* display the article source or NYT if not available */}
                <span className="time">{formatTimeAgo(article.date)}</span> {/* display the time since published */}
                <span className={`sentiment-label ${sentiments[index] || 'neutral'}`}>  {/* display the sentiment label with appropriate styling */}
                  {sentiments[index] ? sentiments[index].charAt(0).toUpperCase() + sentiments[index].slice(1) : 'Neutral'} 
                </span>
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};

export default NewsPanel;
